/** @jsxImportSource @emotion/react */
import react, { useState } from "react";
import { useParams } from "react-router";
import { useNavigate } from "react-router-dom";
import * as ui from "@material-ui/core";
import Pagination from "@material-ui/lab/Pagination";
import * as auth from "../../auth";
import * as rest from "../../rest";
import { definitions } from "../../schema/api";
import { roles } from "../../const/role";
import { Context, DataStore, Sort, sortType } from "../../context/DataStoreContext";
import { List } from "./retrieve/list";
import { Input, UploadingResource } from "./retrieve/input";
import { ClientCompanySelector } from "../../components/client_company_selector";
import { IndicatorButton } from "../../components/indicator_button";
import { Modal } from "../../components/modal";
import { Breadcrumbs } from "./retrieve/breadcrumbs";
import { ShareResourcesPutParam } from "../../rest/share_resource";
import { Folder, InsertDriveFile } from "@material-ui/icons";

const labels = {
  displayCount: "表示件数",
  all: "全",
  dataUnit: "件",
  open: "開く",
  edit: "編集",
  download: "ダウンロード",
  uploadConfirm: (inFile: boolean, inFolder: boolean) => {
    const resourceTypes = [
      ...(inFile ? ["資料"] : []),
      ...(inFolder ? ["フォルダ"] : []),
    ];

    return `${resourceTypes.join("・")}をアップロードします`;
  },
  editConfirm: (isFolder: boolean) => `${isFolder ? "フォルダ" : "資料"}を更新します`,
  delete: "削除",
  deleteConfirm: "チェックした資料を削除しますか？",
  deleteFolderConfirm: "フォルダを削除すると以下のファイルも一括削除されます",
  submit: "送信",
  submitting: "送信中...",
  fileName: "資料名",
  folderName: "フォルダ名",
};

const styles = {
  modal: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    gap: "1rem",
    minWidth: "50vw",
    maxWidth: "calc(100vw - 2rem)",
    ["& img"]: {
      maxWidth: "100%",
      maxHeight: "50vh",
    },
  },
  modalTitle: {
    textAlign: "center" as const,
    fontSize: "1rem",
  },
  modalOperator: {
    display: "flex",
    dustifyContent: "center",
    alignItems: "center",
    gap: "1rem",
  },
  icon: {
    display: "flex",
    justifyContent: "center",
    ["& > svg"]: {
      width: "100px",
      height: "100px",
    },
  },
};

const perPageCandidates = [30, 50, 100];
const defaultItemsPerPage = 30;
const defaultSorts: Sort<definitions["ShareResource"]>[] = [
  { key: "date", order: sortType.desc },
];

const convertSortsToQuery = (sorts: Sort<definitions["ShareResource"]>[]) =>
  [
    "type=folder:desc",
    ...sorts.map(({ key, order }) => `${key}:${order === sortType.desc ? "desc" : "asc"}`),
  ].join(",");

type RetrieveState = {
  initialized: boolean;
  client?: definitions["Client"];
  clients: definitions["Client"][];
  enableUpdate: boolean;
  validated: boolean;
  folder?: DataStore["shareResource"]["listParameters"][number];
  itemsPerPage: number;
  page: number;
  targetFiles: definitions["ShareResource"][];
  newResources: UploadingResource[];
};

function Retrieve() {
  const { state: contextState, dispatch } = react.useContext(Context);
  const shareResourceState = contextState?.shareResource;
  const shareResourceParams = shareResourceState?.listParameters || [];
  const [state, setState] = react.useState<RetrieveState>({
    initialized: false,
    clients: [],
    enableUpdate: false,
    validated: false,
    itemsPerPage: defaultItemsPerPage,
    page: 1,
    targetFiles: [],
    newResources: [],
  });
  const [selectedFile, setSelectedFile] = useState<definitions["ShareResource"] | null>(null);
  const [editting, setEditting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const urlParams = useParams<{ date: string, id: string }>();
  const folderId = parseInt(urlParams.id || "0");
  const role = auth.getRole();
  const clientCompanies = state.clients.flatMap(({ client_company }) => client_company || []);
  const navigation = useNavigate();

  const refreshShareResources = async (forceRefresh = false, changedSorts?: Sort<definitions["ShareResource"]>[]) => {
    if (role?.roleName === "root" && !state.client) {
      return;
    }
    const targetFolder = shareResourceParams.find((param) => param.parentId === folderId);
    if (
      targetFolder &&
      targetFolder.clientId === state.client?.id &&
      targetFolder.page === state.page &&
      targetFolder.perPage === state.itemsPerPage &&
      !forceRefresh
    ) {
      setState({ ...state, folder: targetFolder, targetFiles: [] });

      return;
    }
    const sorts = changedSorts || targetFolder?.sorts || defaultSorts;
    const [list, totalCount, folderChain] = folderId
      ? await (async () => {
        const payload: rest.ShareResourceParentsChildrenGetParam = {};
        payload.limit = state.itemsPerPage;
        payload.offset = (state.page - 1) * state.itemsPerPage;
        payload.sorts = convertSortsToQuery(sorts);
        const shareResourceResponse = await (new rest.ShareResource()).getWithParentsChildren(folderId, payload, auth.getToken());
        const folderChain = shareResourceResponse.parents;
        if (shareResourceResponse.share_resource.type === "folder") {
          folderChain.push(shareResourceResponse.share_resource);
        }

        return [shareResourceResponse.children, shareResourceResponse.total_count, folderChain];
      })()
      : await (async () => {
        const payload: rest.ShareResourcesGetParam = {};
        if (role?.roleName === "root") {
          payload.client_id = state.client!.id;
        }
        payload.limit = state.itemsPerPage;
        payload.offset = (state.page - 1) * state.itemsPerPage;
        payload.sorts = convertSortsToQuery(sorts);
        const { list, total_count } = await (new rest.ShareResource()).getBulk(payload, auth.getToken());

        return [list, total_count, []];
      })();
    if (targetFolder) {
      targetFolder.clientId = state.client?.id;
      targetFolder.list = list;
      targetFolder.page = state.page;
      targetFolder.perPage = state.itemsPerPage;
      targetFolder.totalCount = totalCount;
      targetFolder.folderChain = folderChain;
      targetFolder.sorts = sorts;
      setState({ ...state, folder: targetFolder, targetFiles: [], newResources: [] });
    } else {
      const targetFolder = {
        clientId: state.client?.id,
        parentId: folderId,
        list,
        page: state.page,
        perPage: state.itemsPerPage,
        totalCount,
        folderChain,
        sorts,
      };
      shareResourceParams.push(targetFolder);
      setState({ ...state, folder: targetFolder, targetFiles: [], newResources: [] });
    }

    dispatch?.({ type: "SET_SHARE_RESOURCE_BULK", payload: shareResourceParams });
  };

  const checkValidation = () =>
    !state.newResources.find((item) => (
      !item.name ||
      (item.type !== "folder" && !item.file)
    ));

  const onClickFolder = (folderId: number) => {
    navigation(`/share_resource/retrieve/${folderId || ''}`);
  };

  const onCheckFile = (file: definitions["ShareResource"]) => {
    const targetIndex = state.targetFiles.findIndex((item) => item === file);
    targetIndex >= 0
      ? state.targetFiles.splice(targetIndex, 1)
      : state.targetFiles.push(file);
    setState({ ...state });
  };

  const onCheckBulk = () => {
    const list = state.folder?.list || [];
    const targetFiles = !list.length || state.targetFiles.length === list.length ? [] : [ ...list ];
    setState({ ...state, targetFiles });
  };

  const onChangeSort = async (key: keyof definitions["ShareResource"]) => {
    const folder = state.folder;
    if (!folder) {
      return;
    }
    const sorts = [...(folder.sorts || [])];
    const targetSortIndex = sorts.findIndex((item) => item.key === key);
    if (targetSortIndex >= 0) {
      const [targetSort] = sorts.splice(targetSortIndex, 1);
      targetSort.order = targetSort.order === sortType.desc ? sortType.asc : sortType.desc;
      sorts.unshift(targetSort);
    } else {
      sorts.unshift({ key, order: sortType.asc });
    }
    await refreshShareResources(true, sorts);
  };

  const onShareResourcesChanged = (resources: definitions["ShareResource"][]) => {
    const validated = checkValidation();
    setState({ ...state, newResources: resources, validated });
  };

  const onSubmit = async () => {
    const inFile = !!state.targetFiles.find(({ type }) => type !== "folder");
    const inFolder = !!state.targetFiles.find(({ type }) => type === "folder");
    const message = labels.uploadConfirm(inFile, inFolder);
    if (window.confirm(message)) {
      setSubmitting(true);
      const payload: ShareResourcesPutParam = {
        client_id: state.client?.id || 0,
        parent_id: state.folder?.parentId || 0,
        files: [],
        file_names: [],
        file_types: [],
        file_comments: [],
        folder_names: [],
        folder_comments: [],
      };
      state.newResources.forEach((resource) => {
        if (resource.file) {
          payload.files.push(resource.file);
          payload.file_names.push(resource.name);
          payload.file_types.push(resource.type);
          payload.file_comments.push(resource.comment ?? '');
        } else {
          payload.folder_names.push(resource.name);
          payload.folder_comments.push(resource.comment ?? '');
        }
      });

      try {
        const result = await (new rest.ShareResource()).put(payload, auth.getToken());
        console.log(result);
        await refreshShareResources(true);
      } catch(_error) {
        console.log('E', _error);
      }
      setSubmitting(false);
    }
  };

  const onSubmitEdit = async () => {
    if (!selectedFile) {
      return;
    }
    const message = labels.editConfirm(selectedFile.type === "folder");
    if (window.confirm(message)) {
      setSubmitting(true);
      try {
        await (new rest.ShareResource()).post(selectedFile.id, selectedFile, auth.getToken());
        await refreshShareResources(true);
      } catch(_) {
      }
      setSelectedFile(null);
      setSubmitting(false);
    }
  };

  const onDeleteClicked = async () => {
    const inFolder = !!state.targetFiles.find(({ type }) => type === "folder");
    const message = labels.deleteConfirm + (inFolder ? `\n${labels.deleteFolderConfirm}` : '');
    if (window.confirm(message)) {
      setSubmitting(true);
      try {
        await (new rest.ShareResource()).deleteByIds(
          state.targetFiles.map(({ id }) => id),
          auth.getToken()
        );
        //   cookie.save(
        //     storage.successNotification.key,
        //     "1",
        //     storage.successNotification.expires
        //   );
        //   const path = `/${resources.report.identifier}/${controls.retrieve.identifier}`;
        //   const query = `success=${controls.create.identifier}`;
        //   window.location.href = `${path}?${query}`;
        window.location.reload();
      } catch(_error) {
        // props.showGlobalNotification?.(labels.failure);
      }
      setSubmitting(false);
    }
  };

  const onOpenFile = () => {
    window.open(`/${selectedFile?.path || ""}`, "_blank");
  }

  const onSelectFile = (file: definitions["ShareResource"]) => {
    setSelectedFile({ ...file });
    setEditting(file.type === "folder");
  };

  const onUnSelectFile = () => {
    setSelectedFile(null);
    setEditting(false);
  };

  const onEditFile = <T extends keyof definitions["ShareResource"]>(key: T, value: definitions["ShareResource"][T]) => {
    if (selectedFile) {
      selectedFile[key] = value;
      setSelectedFile({ ...selectedFile });
    }
  };

  const onDownloadBulk = async (fileIndex: number) => {
    const file = state.targetFiles[fileIndex];
    if (!file) {
      return;
    }

    if (file.path) {
      // `fetch` API を使用してダウンロード
      const response = await fetch(`/${file.path}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      // レスポンスヘッダからファイル名を取得
      const contentDisposition = response.headers.get('content-disposition');
      let filenameFromHeader = file.name;
      if (contentDisposition) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filenameFromHeader = matches[1].replace(/['"]/g, '');
        }
      }

      const blob = await response.blob();

      // BlobをURLに変換
      const blobUrl = window.URL.createObjectURL(blob);

      // ダウンロードリンクを作成してクリック
      const linkElem = document.createElement("a");
      linkElem.href = blobUrl;
      linkElem.download = filenameFromHeader;
      document.body.appendChild(linkElem);
      linkElem.click();

      document.body.removeChild(linkElem);
      window.URL.revokeObjectURL(blobUrl);
    }

    if (fileIndex + 1 <= state.targetFiles.length) {
      await onDownloadBulk(fileIndex + 1);
    }
  };

  const onDownloadFile = (file: definitions["ShareResource"]) => {
    const link = document.createElement("a");
    link.href = `/${file.path}`;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(`/${file.path}`);
  };

  const onClientCompanyChanged = (id: number) => {
    const client = (state.clients || []).find((item) => item.client_company_id === id);
    client && setState({ ...state, client, page: 1 });
  };

  react.useEffect(() => {
    refreshShareResources();
  }, [folderId, state.page, state.itemsPerPage, state.client]);

  react.useEffect(() => {
    (async () => {
      if (state.initialized) {
        return;
      }
      state.initialized = true;

      const roleName = role?.roleName || '';
      state.enableUpdate = [
        roles.root.identifier,
        roles.admin.identifier,
        roles.client.identifier,
        roles.client_reader.identifier,
      ].includes(roleName);
      const user = await (new rest.User()).getMe(["Client.ClientCompany"], auth.getToken());
      if (user.client?.id) {
        state.clients = [user.client];
        state.client = user.client;
      } else {
        const clients = await (new rest.Client()).getAll(["ClientCompany"], auth.getToken());
        if (clients.length) {
          state.clients = clients;
          state.client = clients[0];
        }
      }

      setState({ ...state });
    })();
  }, []);

  return (
    <ui.Container>
      <ui.Grid container spacing={4}>
        <ui.Grid item xs={12}></ui.Grid>
        {role?.roleName === roles.root.identifier && (
          <ui.Grid item xs={12} sm={6}>
            <ClientCompanySelector
              clientCompanies={clientCompanies}
              selectedValue={state.client?.client_company_id || 0}
              onChanged={onClientCompanyChanged}
            />
          </ui.Grid>
        )}
        <ui.Grid item xs={12}>
          <Breadcrumbs
            list={state.folder?.folderChain || []}
            onClickFolder={onClickFolder}
          />
        </ui.Grid>
        <ui.Grid
          container
          item
          xs={12}
          justifyContent="flex-end"
          alignItems="center"
          style={{ padding: "0 16px" }}
        >
          <ui.Grid
            container
            item
            spacing={2}
            justifyContent="flex-end"
            alignItems="center"
          >
            <ui.Grid item>
              <ui.Typography variant="caption">
                {labels.displayCount}
              </ui.Typography>
            </ui.Grid>
            {perPageCandidates.map((count) => (
              <ui.Grid item key={`perpage-${count}`}>
                {count === state.itemsPerPage ? (
                  <b>{count}</b>
                ) : (
                  <ui.Link
                    style={{ cursor: "pointer" }}
                    onClick={() => setState({ ...state, itemsPerPage: count })}
                  >
                    {count}
                  </ui.Link>
                )}
              </ui.Grid>
            ))}
            <ui.Grid item>
              <ui.Typography variant="caption">|</ui.Typography>
            </ui.Grid>
            <ui.Grid item>
              <Pagination
                count={Math.ceil((state.folder?.totalCount || 0) / state.itemsPerPage) as number}
                page={state.folder?.page || 1}
                onChange={(_, page) => setState({ ...state, page })}
              />
            </ui.Grid>
            <ui.Grid item>
              <ui.Typography variant="caption">
                {`${labels.all} ${state.folder?.totalCount || 0} ${labels.dataUnit}`}{" "}
              </ui.Typography>
            </ui.Grid>
          </ui.Grid>
        </ui.Grid>
        <ui.Grid item xs={12}>
          <List
            list={state.folder?.list || []}
            sorts={state.folder?.sorts}
            checkedFiles={state.targetFiles}
            enableUpdate={state.enableUpdate}
            onChangeSort={onChangeSort}
            onCheckFile={onCheckFile}
            onClickFolder={onClickFolder}
            onCheckBulk={onCheckBulk}
            onSelectFile={onSelectFile}
            onDownloadFile={onDownloadFile}
            onDeleteBulk={onDeleteClicked}
          />
        </ui.Grid>
        {state.enableUpdate && (
          <>
            <ui.Grid item xs={12}>
              <Input
                resources={state.newResources}
                onFileChanged={onShareResourcesChanged}
              />
            </ui.Grid>
            <ui.Grid container item xs={12} spacing={2} justifyContent="flex-start">
              {!!state.newResources.length && (
                <IndicatorButton
                  indicator={{ label: labels.submitting }}
                  showIndicator={submitting}
                  button={{
                    disabled: !state.validated,
                    onClick: onSubmit,
                    label: labels.submit,
                  }}
                />
              )}
            </ui.Grid>
          </>
        )}
      </ui.Grid>
      {selectedFile && (
        <Modal
          display={true}
          onHide={onUnSelectFile}
        >
          <div css={styles.modal}>
            {editting || (
              <div css={styles.modalTitle}>
                {selectedFile.name}
              </div>
            )}
            {
              selectedFile.type === "folder" ? (
                <div css={styles.icon}>
                  <Folder />
                </div>
              ) : selectedFile.type.match(/^image/) ? (
                <img src={`/${selectedFile.path}`} />
              ) : (
                <div css={styles.icon}>
                  <InsertDriveFile />
                </div>
              )
            }
            {editting ? (
              <>
                <ui.TextField
                  id="share-resource-edit-name"
                  fullWidth
                  label={selectedFile.type === "folder" ? labels.folderName : labels.fileName}
                  value={selectedFile.name}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => onEditFile("name", e.target.value)}
                />
                <div css={styles.modalOperator}>
                  <IndicatorButton
                    indicator={{ label: labels.submitting }}
                    showIndicator={submitting}
                    button={{
                      // disabled: !state.validated,
                      disabled: false,
                      onClick: onSubmitEdit,
                      label: labels.submit,
                    }}
                  />
                </div>
              </>
            ) : (
              <div css={styles.modalOperator}>
                {selectedFile.type.match(/pdf/) && (
                  <ui.Button
                    variant="contained"
                    color="primary"
                    onClick={onOpenFile}
                  >
                    {labels.open}
                  </ui.Button>
                )}
                {/* {state.enableUpdate && (
                  <ui.Button
                    variant="contained"
                    color="primary"
                    onClick={() => setEditting(true)}
                  >
                    {labels.edit}
                  </ui.Button>
                )} */}
              </div>
            )}
          </div>
        </Modal>
      )}
    </ui.Container>
  );
}

export { Retrieve };
